import React from 'react';
import { makeStyles, useTheme } from '@material-ui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation
} from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

const useStyles = makeStyles(theme => ({
  '@keyframes animateIn': {
    from: {
      transform: 'translate(500px, 0)',
      opacity: 1
    },
    to: {
      transform: 'translate(0px, 0px)',
      opacity: 1
    }
  },
  '@keyframes progressBar': {
    from: {
      width: '100%'
    },
    to: {
      width: '0%'
    }
  },
  base: {
    zIndex: 100000,
    animationName: '$animateIn',
    animationDuration: '0.5s',
    background: 'white',
    boxShadow: '0px 4px 8px #0000000f',
    borderRadius: 12,
    border: '1px solid #E0E3E7',
    padding: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '17px',
    width: 500,
    pointerEvents: 'auto',
    position: 'relative',
    overflow: 'hidden'
  },
  info: {
    background: '#e5effa',
    border: '1px solid #abcdf1'
  },
  success: {
    background: '#ebf7ee',
    border: '1px solid #bfe5c8'
  },
  error: {
    background: '#fcedea',
    border: '1px solid #f6cac1'
  },
  warning: {
    background: '#fef7ea',
    border: '1px solid #ebce9c'
  },
  text: {
    fontSize: 14,
    fontWeight: 400,
    color: '#0C152E'
  },
  icon: {
    background: '#0b56fd',
    width: 30,
    flexShrink: 0,
    height: 30,
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    boxShadow:
      '0px -2.46203px 4.10338px 0px rgba(3, 3, 3, 0.10) inset, 0px 1.64135px 4px 0px rgb(0 0 0 / 19%)'
  },
  infoIcon: {
    background: '#0b56fd'
  },
  successIcon: {
    background: '#3fbf62'
  },
  errorIcon: {
    background: '#eb4e2d'
  },
  warningIcon: {
    background: '#ef9400'
  },
  leftItems: {
    display: 'flex',
    alignItems: 'center',
    gap: '17px'
  },
  cancelButton: {
    width: 30,
    height: 30,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#0000006e',
    transition: 'all 0.1s',
    '&:hover': {
      color: '#0C152E',
      background: '#5656561c'
    }
  },
  progressBarContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'rgba(0, 0, 0, 0.1)'
  },
  progressBarFill: {
    height: '100%',
    animationName: '$progressBar',
    animationTimingFunction: 'linear'
  },
  infoProgress: {
    background: '#0b56fd'
  },
  successProgress: {
    background: '#3fbf62'
  },
  errorProgress: {
    background: '#eb4e2d'
  },
  warningProgress: {
    background: '#ef9400'
  }
}));

export default function Toast({ message, onClose, showProgress, timeShown }) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  function toastIcon(variant) {
    switch (variant) {
      case 'success':
        return faCircleCheck;
      case 'info':
        return faCircleInfo;
      case 'error':
        return faCircleExclamation;
      case 'warning':
        return faTriangleExclamation;
      default:
        return faCircleInfo;
    }
  }

  return (
    <div className={`${classes.base} ${classes[message.variant]}`}>
      <div className={classes.leftItems}>
        <div className={`${classes.icon} ${classes[message.variant + 'Icon']}`}>
          <FontAwesomeIcon icon={toastIcon(message.variant)} />
        </div>
        <div className={classes.text}>{message.text}</div>
      </div>
      <div className={classes.cancelButton} onClick={onClose}>
        <FontAwesomeIcon icon={faTimes} />
      </div>
      {showProgress && timeShown && (
        <div className={classes.progressBarContainer}>
          <div 
            className={`${classes.progressBarFill} ${classes[message.variant + 'Progress']}`}
            style={{ animationDuration: `${timeShown}ms` }}
          />
        </div>
      )}
    </div>
  );
}
